/**
 * Video category presets — the Phase 3 creation surface for the category
 * taxonomy (docs/VIDEO_SYSTEM_PLAN.md). Six user-facing categories, each with:
 *
 *  - `chatPrefill`  — a prompt template prefilled into the CHAT composer.
 *    These are product surface: they name the category (so the agent routes
 *    through the `video-categories` builtin skill), expose the key creative
 *    slots in [BRACKETS], and read well enough to send after filling 2-3 slots.
 *  - `studioSeed` / `studioPlaceholder` — MEDIA STUDIO scaffolding. The studio
 *    Videos mode talks straight to a text-to-video model (no agent routing),
 *    so these are written as direct T2V shot prompts, not agent briefs.
 *
 * CLIENT-SAFE and dependency-free (icons are looked up by the consumers so
 * this module can be imported anywhere).
 */

export type VideoCategoryId =
  | 'motion-graphic'
  | 'explainer'
  | 'product-promo'
  | 'site-tour'
  | 'music-video'
  | 'ai-clip';

export interface VideoCategoryPreset {
  id: VideoCategoryId;
  label: string;
  /** One-line description shown under the label in pickers. */
  tagline: string;
  chatPrefill: string;
  studioSeed: string;
  studioPlaceholder: string;
}

export const VIDEO_CATEGORY_PRESETS: VideoCategoryPreset[] = [
  {
    id: 'motion-graphic',
    label: 'Motion graphic',
    tagline: 'Kinetic type, logo stings, stat hits — motion is the message',
    chatPrefill:
      'Make a motion graphic — a 6-second logo sting for [BRAND]. Kinetic typography that lands on "[MESSAGE]", a [VIBE: electric violet on black / glassy minimal / brutalist mono] look, and one decisive final lockup.',
    studioSeed:
      'Sleek motion-graphics sting: bold kinetic typography spelling "[MESSAGE]" over a [COLOR] gradient backdrop, geometric shapes snapping to a beat, crisp studio lighting, final logo lockup for [BRAND].',
    studioPlaceholder:
      'Bold kinetic typography sting: "SHIP FASTER" snapping in over an electric violet gradient, final logo lockup',
  },
  {
    id: 'explainer',
    label: 'Explainer',
    tagline: 'Narrated topic breakdown with invented visuals',
    chatPrefill:
      'Make a faceless explainer video (about 60 seconds, narrated) that explains [TOPIC] for [AUDIENCE]. Use the fast explainer path (scene slots) unless the piece needs bespoke motion. Hook in the first five seconds, 3-5 scenes carried by bold typography and simple diagrams, a [VIBE: calm editorial / energetic startup] look, and end on one clear takeaway.',
    studioSeed:
      'Explainer-style shot for [TOPIC]: clean animated diagram elements assembling themselves over a soft studio backdrop, minimal iconography, gentle camera push-in, bright editorial palette.',
    studioPlaceholder:
      'Clean animated diagram assembling itself over a soft studio backdrop, minimal icons, gentle push-in',
  },
  {
    id: 'product-promo',
    label: 'Product promo',
    tagline: 'Launch or sell a product with a narrated promo',
    chatPrefill:
      'Make a product promo video for [PRODUCT] — [URL or one-line pitch]. Sell the core promise, "[MESSAGE]", in about 45 narrated seconds: open on the problem, reveal the product, hit three highlights, and close with a confident call to action in a [VIBE: premium / playful / technical] tone.',
    studioSeed:
      'Product promo shot: [PRODUCT] on a [SURFACE: brushed-steel plinth], slow dolly-in, dramatic studio lighting with soft reflections, premium [VIBE] color grade.',
    studioPlaceholder:
      'Slow dolly-in on a matte-black smart speaker on a steel plinth, dramatic studio light, premium grade',
  },
  {
    id: 'site-tour',
    label: 'Site tour',
    tagline: 'Walk viewers through a real website, page by page',
    chatPrefill:
      'Make a site tour video of [URL]. Capture the real pages and walk through [WHAT TO SHOW: homepage → key sections → the standout feature] in about 40 narrated seconds, with a clean product-demo feel.',
    studioSeed:
      'Screen-tour style fly-through of a modern [KIND: SaaS landing] page: smooth vertical scroll, UI cards gliding past, cursor highlighting key sections, crisp glassmorphism styling.',
    studioPlaceholder:
      'Smooth fly-through of a modern SaaS landing page, UI cards gliding past, crisp glassmorphism',
  },
  {
    id: 'music-video',
    label: 'Music video',
    tagline: 'Generate a track, then cut visuals to the beat',
    chatPrefill:
      'Make a music video: first generate a [GENRE / MOOD: dark synthwave, 120 BPM] track, then cut a beat-synced visual piece to it — [VISUAL THEME: neon grids and chrome type / ink-wash landscapes], with typography and cuts locked to the rhythm.',
    studioSeed:
      'Music-video loop: [VISUAL THEME: neon-lit chrome shapes] pulsing to a beat, rhythmic camera cuts, high-contrast [PALETTE], seamless loop energy.',
    studioPlaceholder:
      'Neon chrome shapes pulsing to a beat, rhythmic cuts, high-contrast magenta and teal, loopable',
  },
  {
    id: 'ai-clip',
    label: 'AI clip',
    tagline: 'One cinematic AI-generated shot with real-world motion',
    chatPrefill:
      'Generate an AI video clip — one cinematic shot, about 5 seconds: [SCENE: what happens, e.g. a chrome sphere melting into liquid on black glass], [CAMERA: slow dolly-in / orbital], [LIGHT + VIBE: moody rim light, filmic grade]. No on-screen text.',
    studioSeed:
      'One cinematic shot: [SCENE: what happens], [CAMERA: slow orbital move], [LIGHTING: moody rim light], filmic color grade, no on-screen text.',
    studioPlaceholder:
      'Ocean waves crashing over black volcanic rock at dusk, slow aerial orbit, moody film grade',
  },
];
